import { CONSTANTS } from "../../utils/Constants";
import { CACHE_KEYS } from "../local/CacheManager";
import { Method, URLs } from "./Configuration";

interface IMainService {
  url: string;
  method: Method | undefined;
  params?: {};
  headers?: {};
  isAuthenticated?: boolean;
  isFile?: boolean;
}
/**
 * Fetches data from the API using the provided parameters.
 *
 * @template T - The type of the response data.
 * @param {IMainService} params - The parameters for the API request.
 * @param {string} params.url - The URL of the API endpoint.
 * @returns {Promise<T>} A promise that resolves to the response data of type T.
 * @throws {Error} If the response is not ok or if there is an error during the fetch operation.
 */
interface IApiResponse<T> {
  status: number;
  ok: boolean;
  data: T;
}

export const fetchAPI = async <T>({
  url,
  method,
  params = {},
  headers,
  isAuthenticated = false,
  isFile = false,
}: IMainService): Promise<IApiResponse<T>> => {
  try {
    const generatedUrl = isFile ? url : generateURL(url);
    const token = localStorage.getItem(CACHE_KEYS.TOKEN);

    const requestHeaders: HeadersInit = {
      ...(isAuthenticated ? { Authorization: `Bearer ${token}` } : {}),
      ...(headers || {}),
    };

    if (!isFile) {
      requestHeaders["Content-Type"] = "application/json";
    }

    const response = await fetch(generatedUrl, {
      method: method ? method : Method.GET,
      body:
        method === Method.GET
          ? null
          : isFile
            ? (params as FormData)
            : JSON.stringify(params),
      headers: requestHeaders,
    });

    // Handle empty response
    const text = await response.text();
    const data = text ? JSON.parse(text) : ({} as T);

    return {
      status: response.status,
      ok: response.ok,
      data,
    };
  } catch (e) {
    if (e instanceof Error) {
      throw new Error(e.message);
    }
    throw new Error("An unknown error occurred");
  }
};
/**
 * Generates the full URL for the API endpoint.
 *
 * @param {string} route - The route of the API endpoint.
 * @returns {string} The full URL for the API endpoint.
 */
const generateURL = (route: string): string => {
  const baseUrl =
    process.env.NODE_ENV === "production"
      ? URLs.PROD_BASE_URL_API
      : URLs.DEV_BASE_URL_API;
  return `${baseUrl}${route}`;
};
